import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarOff, Plus } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../../app/AuthContext";
import { api } from "../../shared/api/client";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingState,
  FilterBar,
  PageHeader,
  Select,
  StatusBadge,
  cn,
} from "../../shared/ui";
import type {
  HeatmapBucket,
  HeatmapView,
  ResourcePersonCapacity,
  ResourcePlanView,
} from "../../shared/types/api";
import { AllocateDrawer } from "./AllocateDrawer";
import { PersonCapacityDrawer } from "./PersonCapacityDrawer";
import {
  defaultWindow,
  describeUtilisation,
  formatHours,
  formatPercent,
  heatmapClasses,
} from "./resourceVocabulary";

const BUCKETS: { value: HeatmapBucket; label: string }[] = [
  { value: "DAILY", label: "Daily" },
  { value: "WEEKLY", label: "Weekly" },
  { value: "MONTHLY", label: "Monthly" },
  { value: "QUARTERLY", label: "Quarterly" },
];

/**
 * The §44 resource planner and the §45 heatmap.
 *
 * <p>One screen with two views, because they answer the same question at different resolutions
 * and a planner moves between them constantly. The heatmap finds <em>when</em> the trouble is; the
 * list says <em>what</em> it consists of.
 *
 * <p>The grid deliberately shows a period per column rather than one figure for the window. §45
 * asks for that, and the reason is arithmetic: somebody allocated fully for one week of a month
 * is at 100% that week and nothing the rest, not 25% throughout — and only the first reading tells
 * a planner anything.
 */
export function ResourcePlannerPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [window, setWindow] = useState(defaultWindow);
  const [bucket, setBucket] = useState<HeatmapBucket>("WEEKLY");
  const [department, setDepartment] = useState("");
  const [view, setView] = useState<"heatmap" | "list">("heatmap");
  const [drilldown, setDrilldown] = useState<{
    employeeId: string;
    fullName: string;
    from: string;
    to: string;
  } | null>(null);
  const [allocating, setAllocating] = useState(false);

  const query = `from=${window.from}&to=${window.to}${
    department ? `&department=${encodeURIComponent(department)}` : ""
  }`;

  const plan = useQuery({
    queryKey: ["resource-plan", window, department],
    queryFn: () => api.get<ResourcePlanView>(`/api/v1/resources/plan?${query}`),
  });

  const heatmap = useQuery({
    queryKey: ["resource-heatmap", window, bucket, department],
    queryFn: () => api.get<HeatmapView>(`/api/v1/resources/heatmap?${query}&bucket=${bucket}`),
    enabled: view === "heatmap",
  });

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ["resource-plan"] });
    void queryClient.invalidateQueries({ queryKey: ["resource-heatmap"] });
  };

  const departments = [
    ...new Set((plan.data?.people ?? []).map((person) => person.department).filter(Boolean)),
  ].sort() as string[];

  return (
    <>
      <PageHeader
        title="Resource planner"
        subtitle="Who is committed to what, and where that exceeds what exists."
      />

      <FilterBar actions={
          can("RESOURCE_ALLOCATE") && (
            <Button icon={<Plus className="h-4 w-4" />} onClick={() => setAllocating(true)}>
              Allocate
            </Button>
          )
        }>
          <div className="w-40">
            <Field label="From">
              <Input
                type="date"
                value={window.from}
                onChange={(event) =>
                  setWindow((current) => ({ ...current, from: event.target.value }))
                }
              />
            </Field>
          </div>
          <div className="w-40">
            <Field label="To">
              <Input
                type="date"
                value={window.to}
                onChange={(event) =>
                  setWindow((current) => ({ ...current, to: event.target.value }))
                }
              />
            </Field>
          </div>
          <div className="w-36">
            <Field label="Grouped by">
              <Select
                value={bucket}
                onChange={(event) => setBucket(event.target.value as HeatmapBucket)}
                disabled={view !== "heatmap"}
              >
                {BUCKETS.map((entry) => (
                  <option key={entry.value} value={entry.value}>
                    {entry.label}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="w-44">
            <Field label="Department">
              <Select
                value={department}
                onChange={(event) => setDepartment(event.target.value)}
              >
                <option value="">Everyone</option>
                {departments.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          <div className="ml-auto flex gap-1 rounded-lg border border-ink-200 p-0.5">
            {(["heatmap", "list"] as const).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setView(option)}
                className={cn(
                  "rounded-md px-3 py-1.5 text-sm font-medium capitalize transition-colors",
                  view === option
                    ? "bg-brand-600 text-white"
                    : "text-ink-600 hover:bg-ink-100",
                )}
              >
                {option}
              </button>
            ))}
          </div>
      </FilterBar>

      {plan.isError ? (
        <ErrorState error={plan.error} onRetry={() => void plan.refetch()} />
      ) : plan.isPending ? (
        <LoadingState label="Working out capacity…" />
      ) : plan.data.people.length === 0 ? (
        <Card>
          <EmptyState
            title="Nobody to plan"
            message="No active employees match that filter."
          />
        </Card>
      ) : (
        <>
          <div className="mb-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Summary
              label="People"
              value={String(plan.data.headcount)}
              hint={`${formatHours(plan.data.totalAvailableHours)} available`}
            />
            <Summary
              label="Committed"
              value={formatHours(plan.data.totalAllocatedHours)}
              hint={`${formatPercent(plan.data.averageUtilisation)} of capacity`}
            />
            <Summary
              label="Over capacity"
              value={String(plan.data.overallocatedCount)}
              tone={plan.data.overallocatedCount > 0 ? "bad" : undefined}
              hint="§42 shows this rather than blocking it"
            />
            <Summary
              label="Room to spare"
              value={String(plan.data.underallocatedCount)}
              hint="Under 60% of a real working window"
            />
          </div>

          {view === "heatmap" ? (
            <Card>
              <CardHeader
                title="Utilisation"
                subtitle="Committed against available, per person per period. Click any cell for the breakdown."
              />
              <CardBody className="p-0">
                {heatmap.isPending ? (
                  <LoadingState label="Building the grid…" />
                ) : heatmap.isError ? (
                  <ErrorState error={heatmap.error} />
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-sm">
                      <thead>
                        <tr>
                          <th className="sticky left-0 z-10 border-b border-ink-200 bg-surface px-4 py-2 text-left text-xs font-medium text-ink-500">
                            Person
                          </th>
                          {heatmap.data.periodLabels.map((label) => (
                            <th
                              key={label}
                              className="border-b border-ink-200 px-2 py-2 text-center text-xs font-medium text-ink-500"
                            >
                              {label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {heatmap.data.rows.map((row) => (
                          <tr key={row.employeeId}>
                            <td className="sticky left-0 z-10 border-b border-ink-100 bg-surface px-4 py-1.5">
                              <span className="block truncate text-sm text-ink-900">
                                {row.fullName}
                              </span>
                              {row.department && (
                                <span className="block truncate text-xs text-ink-400">
                                  {row.department}
                                </span>
                              )}
                            </td>
                            {row.cells.map((cell) => (
                              <td
                                key={cell.from}
                                className="border-b border-ink-100 px-1 py-1.5 text-center"
                              >
                                <button
                                  type="button"
                                  onClick={() =>
                                    setDrilldown({
                                      employeeId: row.employeeId,
                                      fullName: row.fullName,
                                      from: cell.from,
                                      to: cell.to,
                                    })
                                  }
                                  title={`${cell.label}: ${formatHours(cell.allocatedHours)} committed of ${formatHours(cell.availableHours)} available`}
                                  className={cn(
                                    "tnum w-full rounded px-2 py-1 text-xs font-medium transition-opacity hover:opacity-80",
                                    heatmapClasses(cell),
                                  )}
                                >
                                  {cell.unavailable
                                    ? "away"
                                    : cell.availableHours === 0
                                      ? "—"
                                      : formatPercent(cell.utilisation)}
                                </button>
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardBody>
            </Card>
          ) : (
            <Card>
              <CardHeader
                title="Capacity"
                subtitle={`${window.from} to ${window.to}, worked through §40's formula.`}
              />
              <CardBody className="p-0">
                <ul>
                  {plan.data.people.map((person) => (
                    <PersonRow
                      key={person.employeeId}
                      person={person}
                      onOpen={() =>
                        setDrilldown({
                          employeeId: person.employeeId,
                          fullName: person.fullName,
                          from: window.from,
                          to: window.to,
                        })
                      }
                    />
                  ))}
                </ul>
              </CardBody>
            </Card>
          )}
        </>
      )}

      <PersonCapacityDrawer
        target={drilldown}
        onClose={() => setDrilldown(null)}
        onChanged={reload}
      />
      <AllocateDrawer
        open={allocating}
        onClose={() => setAllocating(false)}
        onSaved={reload}
      />
    </>
  );
}

function PersonRow({
  person,
  onOpen,
}: {
  person: ResourcePersonCapacity;
  onOpen: () => void;
}) {
  const state = describeUtilisation(person.utilisation, person.unavailable);

  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="flex w-full flex-wrap items-center gap-3 border-b border-ink-100 px-5 py-3 text-left transition-colors last:border-0 hover:bg-ink-50"
      >
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium text-ink-900">{person.fullName}</span>
          <span className="block text-xs text-ink-400">
            {person.department ?? "—"} · {person.workingDays} working days
          </span>
        </span>

        {/* The §40 chain, left to right, so the deductions are legible rather than a single
            number somebody has to take on trust. */}
        <span className="tnum hidden shrink-0 text-xs text-ink-500 sm:block">
          {formatHours(person.grossHours)} gross
        </span>
        {person.leaveHours > 0 && (
          <span className="tnum flex shrink-0 items-center gap-1 text-xs text-ink-500">
            <CalendarOff className="h-3 w-3" />−{formatHours(person.leaveHours)}
          </span>
        )}
        {person.reservedHours > 0 && (
          <span className="tnum hidden shrink-0 text-xs text-ink-500 sm:block">
            −{formatHours(person.reservedHours)} reserved
          </span>
        )}

        <span className="tnum w-20 shrink-0 text-right text-sm text-ink-900">
          {formatHours(person.availableHours)}
        </span>
        <span className="tnum w-20 shrink-0 text-right text-sm text-ink-900">
          {formatHours(person.allocatedHours)}
        </span>

        <span className="w-40 shrink-0">
          <span className="flex items-center gap-2">
            <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-ink-100">
              <span
                className={cn(
                  "block h-full rounded-full",
                  person.overallocated ? "bg-red-500" : "bg-brand-500",
                )}
                style={{ width: `${Math.min(100, person.utilisation * 100)}%` }}
              />
            </span>
            <span className="tnum w-10 text-right text-xs text-ink-600">
              {formatPercent(person.utilisation)}
            </span>
          </span>
        </span>

        <StatusBadge tone={state.tone}>{state.label}</StatusBadge>
      </button>
    </li>
  );
}

function Summary({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint: string;
  tone?: "bad";
}) {
  return (
    <Card>
      <CardBody>
        <p className="text-sm text-ink-500">{label}</p>
        <p
          className={cn(
            "tnum font-display mt-0.5 text-2xl font-semibold",
            tone === "bad" ? "text-red-600" : "text-ink-900",
          )}
        >
          {value}
        </p>
        <p className="mt-0.5 text-xs text-ink-400">{hint}</p>
      </CardBody>
    </Card>
  );
}
